import React, { useEffect, useRef } from 'react';
import { string, func, bool, number } from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useCookies } from 'react-cookie';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { pathByRouteName } from '../../util/routes';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { getSalePriceForListing, getSalePriceFromMajorUnit } from '../../util/saleCampaigns';

import { AspectRatioWrapper, NamedLink, ResponsiveImage, FavoriteButton } from '../../components';

import css from './ListingCard.module.css';
import {
  getmarketplaceData,
  // marketplaceData
} from '../../marketplace-data';
const getTitle = title => (title.length > 33 ? title.substring(0, 29) + '...' : title);
const getTitleMobile = title => (title.length > 17 ? title.substring(0, 15) + '...' : title);
const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, salePrice } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  const salePriceData = salePrice ? priceData(salePrice, config.currency, intl) : {};
  const { formattedPrice: formattedSalePrice, priceTitle: salePriceTitle } = salePriceData;
  const hasSalePrice = !!formattedSalePrice && !!formattedPrice;

  return (
    <div className={css.price}>
      {hasSalePrice ? (
        <>
          <div className={css.originalPriceValue} title={priceTitle}>
            {formattedPrice}
          </div>
          <div className={css.salePriceValue} title={salePriceTitle}>
            {formattedSalePrice}
          </div>
        </>
      ) : (
        <div className={css.priceValue} title={priceTitle}>
          {formattedPrice}
        </div>
      )}
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    showPromotedUserTag,
    currentUser,
    isFavorited,
    isFavoriteLoading,
    onToggleFavorite,
    salePriceOverride,
    useSalePriceOverride,
  } = props;
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const favoriteButtonRef = useRef(null);

  // Auto-add pending wishlist item after login redirect
  useEffect(() => {
    const pendingWishlistId = sessionStorage.getItem('pendingWishlistId');
    if (pendingWishlistId && pendingWishlistId === id && currentUser) {
      sessionStorage.removeItem('pendingWishlistId');
      // Only add if not already in wishlist (otherwise toggle would remove it)
      if (!isFavorited) {
        // Delay to let scroll restoration happen first
        setTimeout(() => {
          if (favoriteButtonRef.current) {
            favoriteButtonRef.current.click();
          }
        }, 300);
      }
    }
  }, [currentUser, id]);

  const { title = '', price, publicData, state } = currentListing.attributes;
  const salePrice = useSalePriceOverride
    ? getSalePriceFromMajorUnit(currentListing, salePriceOverride, config.currency)
    : getSalePriceForListing(currentListing, config.currency);

  // Check if listing is out of stock
  const currentStock = currentListing.currentStock?.attributes?.quantity ?? null;
  const isOutOfStock = currentStock === 0 || state === 'closed';

  // Check if listing is from promoted user
  const author = ensureUser(listing.author);
  const isPromotedUser = author?.id?.uuid === process.env.REACT_APP_PROMOTED_USER;

  const classes = classNames(rootClassName || css.root, className, {
    [css.outOfStock]: isOutOfStock,
  });
  const {
    department,
    category,
    subCategory,
    designer,
    size,
    otherBrand,
    refrenceNumber: refrenceId,
  } = publicData || {};
  const [cookies] = useCookies(['language']);
  const marketplaceData = getmarketplaceData(cookies?.language);
  const data = marketplaceData?.find(data => data?.value === department);
  const categoryData = data?.subCategories?.enumOptions?.find(data => data?.value === category);
  const subCategoryData = categoryData?.products?.enumOptions?.find(
    data => data?.value === subCategory
  );
  const designerData = subCategoryData?.brands?.enumOptions?.find(data => data?.value === designer);
  const sizeObject = size ? subCategoryData?.sizes?.enumOptions?.find(d => d.value === size) : null;
  const slug = createSlug(title);

  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  let {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  variantPrefix = 'default';
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];
  const setActivePropsMaybe = setActiveListing
    ? {
      onMouseEnter: () => setActiveListing(currentListing.id),
      onMouseLeave: () => setActiveListing(null),
    }
    : null;

  // Show favorite button if onToggleFavorite is provided (for both logged in and logged out users)
  const showFavoriteButton = !!onToggleFavorite;

  const handleToggleFavorite = () => {
    if (!currentUser) {
      // Redirect to login page if user is not logged in
      // Save scroll position and listing ID so they can be restored/used after login redirect
      sessionStorage.setItem('wishlistScrollY', window.scrollY.toString());
      sessionStorage.setItem('pendingWishlistId', id);
      const loginPath = pathByRouteName('LoginPage', routeConfiguration);
      history.push(loginPath, { from: `${history.location.pathname}${history.location.search}` });
      return;
    }
    if (onToggleFavorite) {
      onToggleFavorite(id);
    }
  };

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
    // params={{ departmentCategory:`${department}-${category}`, designer, subCategory,id }}
    >
      <div className={css.relativeImageWrapper}>
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <div className={css.imageContainer}>
            <LazyImage
              rootClassName={classNames(css.rootForImage, {
                [css.fadedImage]: isOutOfStock,
              })}
              alt={title}
              image={firstImage}
              variants={variants}
              sizes={renderSizes}
            />
          </div>
        </AspectRatioWrapper>

        {isOutOfStock && (
          <div className={css.soldOutOverlay}>
            <span className={css.soldOutText}>
              <FormattedMessage id="ListingCard.soldOut" />
            </span>
          </div>
        )}
        {isPromotedUser && showPromotedUserTag && (
          <div className={css.promotedUserTag}>
            <span className={css.promotedUserTagText}>Dora's Closet</span>
          </div>
        )}
        {showFavoriteButton ? (
          <FavoriteButton
            ref={favoriteButtonRef}
            className={css.favoriteButton}
            isFavorited={isFavorited}
            isLoading={isFavoriteLoading}
            onClick={handleToggleFavorite}
          />
        ) : null}
      </div>
      <div className={css.info}>
        <div className={css.mainInfo}>
          <div className={css.title}>{otherBrand || designerData?.label || designer}</div>
          {/* <p className={css.label}>
            {richText(getTitle(title), {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </p> */}
          <p className={css.labelMobile}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </p>
          {sizeObject ? (
            <p className={css.sizeContainer}>
              {intl.formatMessage({ id: 'ListingCard.ListingCard.sizeLabel' })}:{' '}
              <span className={css.size}>{sizeObject?.label || size}</span>
            </p>
          ) : (
            <p className={css.sizeContainerIfNo}>.</p>
          )}

          {/* {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null} */}
        </div>
        <PriceMaybe
          price={price}
          publicData={publicData}
          config={config}
          intl={intl}
          salePrice={salePrice}
        />
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
  showPromotedUserTag: false,
  currentUser: null,
  isFavorited: false,
  isFavoriteLoading: false,
  onToggleFavorite: null,
  salePriceOverride: null,
  useSalePriceOverride: false,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,
  showPromotedUserTag: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,

  // Favorite functionality
  currentUser: propTypes.currentUser,
  isFavorited: bool,
  isFavoriteLoading: bool,
  onToggleFavorite: func,
  salePriceOverride: number,
  useSalePriceOverride: bool,
};

export default injectIntl(ListingCardComponent);
